import { createFileRoute, useRouteContext } from '@tanstack/react-router';
import { Button, Card, Divider, Flex, Form, Input, message, Typography } from 'antd';
import { getAuth, updatePassword, verifyBeforeUpdateEmail } from 'firebase/auth';
import { Lock, Mail } from 'lucide-react';
import { useState } from 'react';

import {
  CREDENTIAL_TOO_OLD_USER_MESSAGE,
  isCredentialTooOldError,
} from '../../../../modules/auth/is-credential-too-old-error';
import { SettingsLayoutContent } from '../../../../ui/components/layout';
import { PasswordMeter } from '../../../../ui/components/password-meter';
import { useUnsavedChangesGuard } from '../../../../ui/hooks';

type ChangeEmailFormValues = {
  email: string;
};

type ChangePasswordFormValues = {
  password: string;
  passwordConfirmation: string;
};

const SuperadminSettingsSecurityRoute = () => {
  const [changeEmailForm] = Form.useForm<ChangeEmailFormValues>();
  const [changePasswordForm] = Form.useForm<ChangePasswordFormValues>();
  const [isSendingEmailChange, setIsSendingEmailChange] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  Form.useWatch([], changeEmailForm);
  Form.useWatch([], changePasswordForm);
  useUnsavedChangesGuard(
    () => changeEmailForm.isFieldsTouched() || changePasswordForm.isFieldsTouched(),
  );

  const { account } = useRouteContext({ from: '/_authenticated' });

  const handleChangeEmail = async (values: ChangeEmailFormValues) => {
    const user = getAuth().currentUser;
    if (!user) {
      message.error('Please sign in again and retry.');
      return;
    }

    const nextEmail = values.email.trim().toLowerCase();
    if (nextEmail === user.email?.trim().toLowerCase()) {
      message.info('This is already your current email address.');
      return;
    }

    try {
      setIsSendingEmailChange(true);
      await verifyBeforeUpdateEmail(user, nextEmail, {
        url: `${window.location.origin}/auth/action?mode=changeEmail`,
        handleCodeInApp: true,
      });

      message.success('Verification email sent. Confirm it to finish changing your email.');
      changeEmailForm.resetFields();
    } catch (error) {
      console.error('Error sending email change verification:', error);
      if (isCredentialTooOldError(error)) {
        message.error(CREDENTIAL_TOO_OLD_USER_MESSAGE);
      } else {
        message.error('Failed to start email change. Please try again.');
      }
    } finally {
      setIsSendingEmailChange(false);
    }
  };

  const handleChangePassword = async (values: ChangePasswordFormValues) => {
    const user = getAuth().currentUser;
    if (!user) {
      message.error('Please sign in again and retry.');
      return;
    }

    try {
      setIsChangingPassword(true);
      await updatePassword(user, values.password);
      message.success('Password changed.');
      changePasswordForm.resetFields();
    } catch (error) {
      console.error('Error changing password:', error);
      if (isCredentialTooOldError(error)) {
        message.error(CREDENTIAL_TOO_OLD_USER_MESSAGE);
      } else {
        message.error('Failed to change password. Please try again.');
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <SettingsLayoutContent title="Security" description="Change your login email and password.">
      <Flex vertical gap={16}>
        <Card title="Change Email Address">
          <Typography.Paragraph type="secondary">
            Current email: <Typography.Text strong>{account.email}</Typography.Text>
          </Typography.Paragraph>

          <Form
            form={changeEmailForm}
            layout="vertical"
            validateTrigger="onSubmit"
            onFinish={handleChangeEmail}
          >
            <Form.Item
              name="email"
              label="New email address"
              rules={[
                { required: true, message: 'Please enter a new email address' },
                { type: 'email', message: 'Please enter a valid email address' },
              ]}
            >
              <Input size="large" placeholder="name@company.com" prefix={<Mail size="1em" />} />
            </Form.Item>

            <Flex justify="flex-end">
              <Button type="primary" htmlType="submit" loading={isSendingEmailChange}>
                Send verification link
              </Button>
            </Flex>
          </Form>
        </Card>

        <Divider dashed />

        <Card title="Change Password">
          <Form
            form={changePasswordForm}
            layout="vertical"
            validateTrigger="onSubmit"
            onFinish={handleChangePassword}
          >
            <Form.Item
              name="password"
              label="New password"
              rules={[
                { required: true, message: 'Please enter a new password' },
                { min: 12, message: 'Password must be at least 12 characters long' },
              ]}
            >
              <Input.Password
                size="large"
                placeholder="New password"
                prefix={<Lock size="1em" />}
              />
            </Form.Item>

            <Form.Item noStyle dependencies={['password']}>
              {({ getFieldValue }) => {
                const password = getFieldValue('password')?.trim() ?? '';

                return password ? (
                  <PasswordMeter
                    password={password}
                    penalizedTokens={[account.firstName, account.lastName, account.email].filter(
                      Boolean,
                    )}
                  />
                ) : null;
              }}
            </Form.Item>

            <Form.Item
              name="passwordConfirmation"
              label="Confirm new password"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your new password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }

                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password
                size="large"
                placeholder="Confirm password"
                prefix={<Lock size="1em" />}
              />
            </Form.Item>

            <Flex justify="flex-end">
              <Button type="primary" htmlType="submit" loading={isChangingPassword}>
                Update password
              </Button>
            </Flex>
          </Form>
        </Card>
      </Flex>
    </SettingsLayoutContent>
  );
};

export const Route = createFileRoute('/_authenticated/superadmin/settings/security')({
  component: SuperadminSettingsSecurityRoute,
});
